import i18n from '@/i18n';

import { getAppFormatLocale, getNumberSuffixes } from './locale';

type Magnitude = { divisor: number; suffix: string };

const getMagnitude = (num: number, language?: string): Magnitude => {
  const suffixes = getNumberSuffixes(language);
  const abs = Math.abs(num);
  if (abs >= 1_000_000_000) return { divisor: 1_000_000_000, suffix: suffixes.billion };
  if (abs >= 1_000_000) return { divisor: 1_000_000, suffix: suffixes.million };
  if (abs >= 100_000) return { divisor: 1_000, suffix: suffixes.thousand };
  return { divisor: 1, suffix: '' };
};

const formatFull = (num: number, locale: string): string =>
  Math.round(num)
    .toLocaleString(locale, {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\s/g, ' ');

const formatWithMagnitude = (num: number, mag: Magnitude, locale: string): string => {
  if (mag.divisor === 1) return formatFull(num, locale);
  const value = num / mag.divisor;
  const formatted = value.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  return `${formatted} ${mag.suffix}`;
};

export const formatNumber = (num: number, condensed = true): string => {
  const locale = getAppFormatLocale();
  if (!condensed) return formatFull(num, locale);
  return formatWithMagnitude(num, getMagnitude(num, i18n.language), locale);
};

export const formatRange = (
  min: number | null,
  max: number | null,
  options?: {
    currency?: string;
    condensed?: boolean;
    fromLabel?: string;
    toLabel?: string;
    emptyLabel?: string;
  },
): string => {
  const {
    currency = '',
    condensed = true,
    fromLabel = '',
    toLabel = '',
    emptyLabel = '-',
  } = options ?? {};

  const locale = getAppFormatLocale();

  if (min == null && max == null) return emptyLabel;

  const suffix = currency ? ` ${currency}` : '';

  if (min != null && max != null) {
    if (!condensed) return `${formatFull(min, locale)} - ${formatFull(max, locale)}${suffix}`;

    const mag = [getMagnitude(min, i18n.language), getMagnitude(max, i18n.language)].sort(
      (a, b) => b.divisor - a.divisor,
    )[0];
    return `${formatWithMagnitude(min, mag, locale)} - ${formatWithMagnitude(max, mag, locale)}${suffix}`;
  }

  const fmt = (n: number) => (condensed ? formatNumber(n, true) : formatFull(n, locale));

  if (min != null) {
    const prefix = fromLabel ? `${fromLabel} ` : '';
    return `${prefix}${fmt(min)}${suffix}`;
  }

  const prefix = toLabel ? `${toLabel} ` : '';
  return `${prefix}${fmt(max as number)}${suffix}`;
};
